import React, { useState, useMemo } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    Tooltip,
    Stack,
    LinearProgress
} from '@mui/material';
import dayjs from 'dayjs';

import ReactTablePdfDownload from 'src/shared/components/table/components/ReactTablePdfDownload';
import ReactTableXLSXDownload from 'src/shared/components/table/components/ReactTableXLSXDownload';
import OrderReportFilters from '../component/ReportFilter';
import PndRestApi from 'src/utils/pnd/PndRestApi';
import { showNotification } from 'src/shared/components/Notification';
import { ERROR_MESSAGES } from 'src/utils/pnd/PndConstants';

const OrderReport = () => {
    const [loading, setLoading] = useState(false);
    const [isReport, setReports] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [hub, setHub] = useState('');
    const [rider, setRider] = useState('');
    const [hubs, setHubs] = useState([]);
    const [riders, setRiders] = useState([]);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);

    const reportColumns = [
        { id: 'storeName', label: 'Store Name' },
        { id: 'hybrisOrderNo', label: 'Hybris No.' },
        { id: 'salesOrderNo', label: 'SAP Order No.' },
        { id: 'orderNo', label: 'Delivery Order No' },
        { id: 'riderName', label: 'Driver Name' },
        { id: 'plateNo', label: 'Vehicle No.' },
        { id: 'areaName', label: 'Delivery Area' },
        { id: 'emiratesName', label: 'Emirate' },
        { id: 'orderPlacedTime', label: 'Order Placement DateTime' },
        { id: 'pgiTime', label: 'PGI DateTime' },
        { id: 'requestedTime', label: 'Req. Delivery Date' },
        { id: 'requestedTimeSlot', label: 'Req. Delivery Time Slot' },
        { id: 'endDeliveryTime', label: 'Actual Delivery DateTime' },
        { id: 'customerName', label: 'Customer Name' },
        { id: 'orderStatus', label: 'Delivery Status' },
        { id: 'paymentMethod', label: 'Payment Method' },
        { id: 'orderAmount', label: 'Order Value' }
    ];

    const replaceColons = (column, value) => {
        const columnsToReplace = ['riderName', 'storeName'];
        if (columnsToReplace.includes(column) && typeof value === 'string') {
            return value.replace(/:/g, ' ');
        }
        return value;
    };

    const handleSearch = async () => {
        const payload = {
            selectedHubId: hub || null,
            selectedRiderId: rider || null,
            fromDate: fromDate ? dayjs(fromDate).format('YYYY-MM-DDTHH:mm:ss') : null,
            toDate: toDate ? dayjs(toDate).format('YYYY-MM-DDTHH:mm:ss') : null,
        };

        setLoading(true);
        setReports([]);
        setPage(0);

        try {
            const response = await PndRestApi.GenerateOrderReport(payload);
            if (response.status === 200 && response.data.status === 200) {
                setReports(response.data.data?.content || []);
            } else {
                showNotification(
                    "theme-light",
                    "ltr",
                    `Code: ${response?.data?.status || response?.status}`,
                    response?.data?.message || ERROR_MESSAGES.tryCatchError
                );
            }
        } catch (error) {
            console.error(error);
            showNotification(
                "theme-light",
                "ltr",
                "500",
                ERROR_MESSAGES.tryCatchError
            );
        } finally {
            setLoading(false);
        }
    };

    const filteredReportForExport = useMemo(() => {
        return isReport.map(row => {
            return reportColumns.reduce((obj, col) => {
                obj[col.label] = row[col.id] || '';
                return obj;
            }, {});
        });
    }, [isReport]);

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h5" gutterBottom className="fw-bold text-success mb-3">
                PICK N DROP ORDER REPORT
            </Typography>
            <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mb: 2 }}>
                <ReactTableXLSXDownload
                    csvData={filteredReportForExport}
                    fileName="Order_Report"
                    title="PICK N DROP ORDER REPORT"
                />
                <ReactTablePdfDownload
                    csvData={filteredReportForExport}
                    fileName="Order_Report"
                    title="PICK N DROP ORDER REPORT"
                    orientation="landscape"
                />
            </Stack>

            <OrderReportFilters
                hub={hub}
                setHub={setHub}
                hubs={hubs}
                setHubs={setHubs}
                rider={rider}
                setRider={setRider}
                riders={riders}
                setRiders={setRiders}
                fromDate={fromDate}
                setFromDate={setFromDate}
                toDate={toDate}
                setToDate={setToDate}
                onSearch={handleSearch}
            />

            <TableContainer component={Paper} sx={{ maxHeight: '600px', overflowY: 'auto', border: '1px solid #eee' }}>
                {loading && <LinearProgress color="primary" />}
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell
                                sx={{
                                    backgroundColor: 'rgb(50 202 115)',
                                    color: 'white',
                                    fontWeight: 'bold',
                                    width: '60px'
                                }}
                            >
                                Sr. No.
                            </TableCell>
                            {reportColumns.map((col) => (
                                <TableCell
                                    key={col.id}
                                    sx={{
                                        backgroundColor: 'rgb(50 202 115)',
                                        color: 'white',
                                        fontWeight: 'bold',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {col.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isReport && isReport.length > 0 ? (
                            isReport
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row, index) => (
                                    <TableRow
                                        key={index}
                                        sx={{ backgroundColor: index % 2 === 0 ? 'transparent' : '#f9f9f9' }}
                                    >
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            {(page * rowsPerPage) + index + 1}
                                        </TableCell>
                                        {reportColumns.map((col) => {
                                            const cellValue = row[col.id];
                                            const displayValue = (cellValue === ':') ? '' : replaceColons(col.id, cellValue);

                                            return (
                                                <TableCell
                                                    key={col.id}
                                                    sx={{
                                                        whiteSpace: 'nowrap',
                                                        fontSize: '0.82rem'
                                                    }}
                                                >
                                                    <Tooltip title={String(cellValue || "")} arrow>
                                                        <span>{String(displayValue || "")}</span>
                                                    </Tooltip>
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={reportColumns.length + 1} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                    No data available in table
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            {isReport.length > 0 && (
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={isReport.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, p) => setPage(p)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            )}
        </Box>
    );
};

export default OrderReport;
